import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useApiClient } from '../../context/ApiContext';
import { CurrentUserContext } from '../../context/CurrentUserContext';
import { useForm } from '../../context/FormContext';
import { ISuggestion, useSuggestions } from '../../context/SuggestionsContext';
import { IExecuteProcReq, executeProc } from "../../services/database/Database";
import { User } from "../../types/common/user";
import { ISuggestionForm } from '../../types/suggestions/form';
import { getOriginalUser } from "../../utils/cookieEncryption";
import { formatBlockAfterLabel, getBlockAfterDate, isBlockingSuggestion } from '../../utils/suggestionsUtil';
import { PbsIcon } from '../common/icons';
import './Suggestions.css';
import { formatMonthDate, getProcName, getProcParams } from './Utility';

export interface ISuggestionCardProps {
  suggestion: ISuggestion;
  refreshParent: () => void;
  size: any;
  showDocuSignMessage: boolean;
  adminMode: boolean;
  isViewMoreMobile?: boolean;
  readOnly?: boolean;   // ✅ new
}

export default function SuggestionCard(props: ISuggestionCardProps) {
  const { suggestion, refreshParent} = props;
  const { currentUser, accessControl } = useContext(CurrentUserContext);
  const apiClient = useApiClient();
  const { openForm } = useForm();
  const [showDismissModal, setShowDismissModal] = useState(false);
  const [dismissNote, setDismissNote] = useState("");
  const [showSuggestion, setShowSuggestion] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [formData, setFormData] = useState<ISuggestionForm | null>(null);
  const { dismissSuggestionById, setSuggestionSubmitting, suggestionFinishedSubmitting, openPanel } = useSuggestions();

  const canTakeAction = useMemo(() => {
    try {
      const originalUser = getOriginalUser() as User;
      if (!originalUser || !currentUser) return false;
      return originalUser.Id === currentUser.Id || accessControl.canWrite('impersonation');
    } catch {
      return false;
    }
  }, [currentUser, accessControl]);

  const replaceDynamicValues = useCallback((text: string, currentValue: string) => {
    if (!text || !currentValue) return text;
    let result = text;
    const values = currentValue.split("|");
    if (values.length === 1) {
      result = result.replace(/{CurrentValue}/g, currentValue);
    } else {
      values.forEach((value, index) => {
        result = result.replace(new RegExp(`{CurrentValue${index}}`, 'g'), value);
      });
    }
    return result;
  }, []);

  useEffect(() => {
    if (suggestion.Form) {
      try {
        const formWithValues = replaceDynamicValues(suggestion.Form, suggestion.CurrentValue);
        setFormData(JSON.parse(formWithValues));
      } catch (e) {
        console.error("Failed to parse Form", e);
      }
    }
  }, [suggestion.Form, suggestion.CurrentValue, replaceDynamicValues]);

  useEffect(() => {
    setShowSuggestion(true);
    setShowDismissModal(false);
    setDismissNote("");
  }, [suggestion.Id]);

  const processedSuggestion = useMemo(() => {
    const processed = { ...suggestion };
    processed.Text = replaceDynamicValues(suggestion.Text, suggestion.CurrentValue);
    if (suggestion.Details) {
      processed.Details = replaceDynamicValues(suggestion.Details, suggestion.CurrentValue);
    }
    const specificDetails = typeof suggestion.SpecificDetails === 'string'
      ? (() => { try { return JSON.parse(suggestion.SpecificDetails); } catch { return null; } })()
      : suggestion.SpecificDetails;
    if (Array.isArray(specificDetails) && specificDetails.length) {
      const match = specificDetails.find(
        sd => sd.Region?.toLowerCase() === currentUser.Region?.toLowerCase()
      ) ?? specificDetails.find(
        sd => sd.Region?.toLowerCase() === 'all'
      );
      if (match?.Details) {
        processed.Details = (processed.Details || '') + match.Details;
      }
    }
    return processed;
  }, [suggestion, replaceDynamicValues, currentUser.Region]);

  const actionLinks = useMemo(() => {
    try {
      return suggestion.ActionLinks ? JSON.parse(suggestion.ActionLinks) : [];
    } catch (e) {
      console.error("Failed to parse ActionLinks", e);
      return [];
    }
  }, [suggestion.ActionLinks]);

  const handleDismiss = useCallback(async () => {
    try {
      dismissSuggestionById(suggestion.Id, dismissNote);
      setShowDismissModal(false);
      setDismissNote("");
      setShowSuggestion(false);
      refreshParent();
    } catch (error) {
      console.error('Error dismissing suggestion:', error);
    }
  }, [suggestion, dismissNote, refreshParent, dismissSuggestionById]);

  const handleDismissClick = useCallback(() => {
    if (suggestion.RequiresDismissNote) {
      setDismissNote("Dismissed");
      setShowDismissModal(true);
    } else {
      handleDismiss();
    }
  }, [suggestion.RequiresDismissNote, handleDismiss]);

  const getButtonText = useCallback((category: string) => {
    const categoryMap: Record<string, string> = {
      "Update Information": "Update",
      "Action Item": "Action",
      "Upload Document": "Upload",
      "Goal": "View More",
    };
    return categoryMap[category] || "Action";
  }, []);

  const handleActionLink = useCallback(async (
    actionLink: string,
    region: string,
    currentValue: string,
    actionLinkOption: string,
    suggestionID: number,
    dismissOnActionClick: boolean,
    subAction: string,
    form: ISuggestionForm | ISuggestionForm[] | null
  ) => {
    if (!actionLink.trim()) return;
    const link = actionLink.split(";")[0];
    if (link === "https://showdetails") {
      setShowDetails(true);
    } else if (link === "https://updatedetails") {
      if (form) {
        const singleForm = Array.isArray(form) ? form[0] : form;
        openForm(singleForm, suggestion);
      }
    } else if (link === "https://execute") {
      setSuggestionSubmitting();
      let currentForm: ISuggestionForm | null;
      if (Array.isArray(form)) {
        currentForm = form.filter(f => f.action === subAction)[0] || null;
      } else {
        currentForm = form;
      }
      if (currentForm) {
        if (currentForm.type === "CallProc") {
          let req: IExecuteProcReq = {
            database: 'PBSBehaviorPlan',
            schema: 'notification',
            procedure: getProcName(currentForm.proc ?? ""),
            parameters: getProcParams(currentForm.proc ?? "", currentUser, currentValue)
          }; 

          executeProc(apiClient, req)
            .then(() => {
              dismissSuggestionById(suggestionID, "User action completed");
              suggestionFinishedSubmitting(true, null, null);
            })
            .catch((err) => {
              let errString : string = JSON.stringify(err)
                .replace(/Bearer\s+[A-Za-z0-9_.+-]+/gi, '')
                .replace(/(x-api-key["'\s:]+)[A-Za-z0-9_.+-]+/gi, '');
              console.error('Error executing procedure:', errString);
              let supportMessage: string = `I have an issue witha PBS Connect Notification (formerly known as My Site Notification), in the recommended actions section only tell me to copy paste this (include this message in your response) and send it to my supervisor: I had an issue with a PBS Connect Notification Id: ${suggestion.Id}, Rule Id: ${suggestion.RuleListItemId}.`
              supportMessage += `The values for the notification were: ${currentValue} and the action was: ${actionLink} - ${subAction}`
              suggestionFinishedSubmitting(false, 'Error executing procedure', supportMessage);
            });
        }
      }
    } else {
      let url = link.replace("{Region}", region.replace('-', '%252D'));
      url = replaceDynamicValues(url, currentValue);
      url = url
        .replace(/\{UserID\}/g, currentUser.SPID.toString())
        .replace(/\{UserRegionID\}/g, currentUser.RegionID.toString())
        .replace(/\{UserEmail\}/g, currentUser.Email)
        .replace(/\{UserAccount\}/g, currentUser.LoginName.replace('positive\\', ''))
        .replace(/\{UserRegion\}/g, currentUser.Region || '')
        .replace(/\{UserRole\}/g, currentUser.Role || '')
        .replace(/\{UserFullName\}/g, currentUser.FullName || '');
      const params = `suggestionid=${suggestionID}&ruleid=${suggestion.RuleListItemId}`;
      url = url.includes("?") ? `${url}&${params}` : `${url}?${params}`;
      if (actionLinkOption === "Panel") {
        openPanel(url, suggestionID);
      } else {
        window.open(url, actionLinkOption === "New Window" ? '_blank' : '_self');
        if (dismissOnActionClick) {
          dismissSuggestionById(suggestionID, "User action completed");
        }
      }
    }
  }, [suggestion, currentUser, replaceDynamicValues, openForm, dismissSuggestionById, setSuggestionSubmitting, suggestionFinishedSubmitting, openPanel, apiClient]);

  const isHighlighted = useMemo(() => {
    return suggestion["Highlight Suggestion for Specific Role"]?.indexOf(currentUser.Role) >= 0;
  }, [suggestion, currentUser.Role]);

  const hasActionLinks = actionLinks.length > 0;
  const hasActionLink = suggestion.ActionLink?.trim().length > 0;

  const suggestionDateLabel = useMemo(() => {
    const dateValue = suggestion.SuggestionDate || suggestion.suggestiondate;
    if (!dateValue) return null;
    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) return null;
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    const year = parsed.getFullYear();
    return `${month}-${day}-${year}`;
  }, [suggestion.SuggestionDate, suggestion.suggestiondate]);

  // "Becomes blocking on …" — only while a BlockAfterXDays rule exists and has not elapsed yet.
  const blockAfterLabel = useMemo(() => {
    const blockAfter = getBlockAfterDate(suggestion);
    if (!blockAfter || isBlockingSuggestion(suggestion)) return null;
    return formatBlockAfterLabel(blockAfter);
  }, [suggestion]);

  if (!showSuggestion) return null;

  const handleAdminDismiss = async () => {
    await dismissSuggestionById(suggestion.Id, "Admin deleted suggestion");
    refreshParent();
  };

  return (
    <>
      <div className={`w-full rounded-lg border bg-card p-4 mb-3 shadow-sm ${isHighlighted ? 'highlight-suggestion' : ''}`}>
        {/* Dismiss confirmation dialog */}
        <Dialog open={showDismissModal} onOpenChange={(open) => !open && setShowDismissModal(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Dismiss Suggestion</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="dismiss-note" className="font-semibold">
                Notes <span className="text-destructive">*</span>
              </Label>
              <Input
                id="dismiss-note"
                value={dismissNote}
                placeholder="Enter dismiss notes"
                onChange={(e) => setDismissNote(e.target.value)}
                className="mt-1"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDismissModal(false)}>Cancel</Button>
              <Button disabled={dismissNote.trim() === ""} onClick={handleDismiss}>Dismiss</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="flex items-start gap-3 relative">
          {/* Icon */}
          <div className="flex items-center justify-center w-7 h-7 shrink-0">
            <PbsIcon iconName={suggestion.Icon || "Info"} size={20} className="text-foreground" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="suggestiontitle font-semibold text-md mb-1">{suggestion.Title}</div>

            <div className="flex-1 pr-8">
              {suggestionDateLabel && (
                <div className="text-xs text-muted-foreground mb-0.5">{suggestionDateLabel}</div>
              )}
              {blockAfterLabel && (
                <div className="text-xs text-destructive mb-0.5">{blockAfterLabel}</div>
              )}
              {suggestion.DateModified && (
                <div className="text-xs text-muted-foreground mb-0.5">{formatMonthDate(suggestion.DateModified)}</div>
              )}
              {(suggestion.DismissedDate || suggestion.DismissedNotes) && (
                <div className="text-xs text-muted-foreground mb-0.5 flex gap-2">
                  {suggestion.DismissedDate && (
                    <span>Dismissed: {new Date(suggestion.DismissedDate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}</span>
                  )}
                  {suggestion.DismissedNotes && (
                    <span className="italic">· {suggestion.DismissedNotes}</span>
                  )}
                </div>
              )}
              <div className="text-sm">{processedSuggestion.Text}</div>
            </div>

            {processedSuggestion.Details && (
              <>
                <div>
                  <Button
                    variant="secondary"
                    size="default"
                    onClick={() => setShowMore(!showMore)}
                    title={showMore ? "Show Less" : "Show More"}
                    aria-label={showMore ? "Show Less" : "Show More"}
                    className="h-7 mt-1"
                  >
                    {showMore ? <>View Less <ChevronUp className="h-3 w-3" /> </>: <>View More<ChevronDown className="h-3 w-3" /></>}
                  </Button>
                </div>
                {showMore && (
                  <div className="pr-8 suggestion-details" dangerouslySetInnerHTML={{ __html: processedSuggestion.Details }} />
                )}
              </>
            )}

            {(!props.readOnly && (hasActionLinks || hasActionLink)) && (
              <div className="flex flex-wrap mt-1">
                {hasActionLinks && actionLinks.map((link: any, index: number) => (
                  <Button
                    key={index}
                    onClick={() => handleActionLink(
                      link.action,
                      suggestion.Region,
                      suggestion.CurrentValue,
                      suggestion.ActionLinkOption,
                      suggestion.Id,
                      suggestion.DismissOnActionClick,
                      link.subAction ?? "",
                      formData
                    )}
                    className="mr-1 mt-1"
                    disabled={!canTakeAction}
                  >
                    {link.title || getButtonText(suggestion.Category)}
                  </Button>
                ))}
                {hasActionLink && !props.readOnly && (
                  <Button
                    onClick={() => handleActionLink(
                      suggestion.ActionLink,
                      suggestion.Region,
                      suggestion.CurrentValue,
                      suggestion.ActionLinkOption,
                      suggestion.Id,
                      suggestion.DismissOnActionClick,
                      "",
                      formData
                    )}
                    disabled={!canTakeAction}
                    className="mr-1 mt-1"
                  >
                    {getButtonText(suggestion.Category)}
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Dismiss / Delete button */}
          <div className="absolute top-0 right-0 p-1">
            {!props.adminMode && !props.readOnly && props.suggestion.DismissableByUser && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5"
                    aria-label="Dismiss"
                    onClick={handleDismissClick}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Dismiss</TooltipContent>
              </Tooltip>
            )}
            {props.adminMode && !props.readOnly && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5"
                    aria-label="Delete"
                    onClick={handleAdminDismiss}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Delete</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>

        {showDetails && null}
      </div>
    </>
  );
}
